// Recensement place par place (D-305), hors connexion : les places de l'îlot dans l'ordre rangée, rang ;
// pour chacune, occupée ou libre, occupant, téléphone et activité, photo, confirmation ; l'opération
// recensement_place part par la file de synchronisation et l'écran passe à la place suivante.
import { schemaChargeRecensementPlace } from '@cc/contrats';
import { Bandeau, Bouton, CaseACocher, Champ } from '@cc/ui';
import { useState } from 'react';
import { messageErreur } from '../../api/erreurs';
import { uuidV7 } from '../../terrain/identifiants';
import { noterSaisie, type IlotEmporte, type PlaceIlot } from '../../terrain/ilots';
import { synchroniseurPartage } from '../../terrain/synchronisation';
import { Section } from './commun';
import { SaisiePhotos, versPhotosAEnregistrer, type PhotoSaisie } from './SaisiePhotos';
import styles from './terrain.module.css';

function situation(p: PlaceIlot, e: IlotEmporte): string {
  const s = e.saisies[p.id];
  if (s)
    return s.statut === 'libre'
      ? 'Libre (saisie de l’appareil)'
      : `${s.occupant ?? ''} (saisie de l’appareil)`;
  if (p.recensement)
    return p.recensement.statut === 'libre'
      ? 'Libre (recensée)'
      : `${p.recensement.occupant ?? ''} (recensée)`;
  return 'À recenser';
}

export function RecensementIlot({
  emporte,
  onSaisie,
}: {
  emporte: IlotEmporte;
  onSaisie: () => void;
}) {
  const premiere = emporte.places.findIndex((p) => !p.recensement && !emporte.saisies[p.id]);
  const [index, setIndex] = useState(premiere < 0 ? 0 : premiere);
  const [statut, setStatut] = useState<'occupee' | 'libre'>('occupee');
  const [nom, setNom] = useState('');
  const [telephone, setTelephone] = useState('');
  const [activite, setActivite] = useState('');
  const [photos, setPhotos] = useState<PhotoSaisie[]>([]);
  const [confirme, setConfirme] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const place = emporte.places[index];
  if (!place) return null;

  const enregistrer = async () => {
    setErreur(null);
    setEnCours(true);
    try {
      const charge = schemaChargeRecensementPlace.parse({
        statut,
        ...(statut === 'occupee'
          ? {
              occupant: {
                nom,
                ...(telephone.trim() ? { telephone } : {}),
                ...(activite.trim() ? { activite } : {}),
              },
            }
          : {}),
        ...(photos[0] ? { photo_id_client: photos[0].id_client } : {}),
        confirme,
      });
      await synchroniseurPartage().enfiler(
        {
          id_client: uuidV7(),
          type: 'recensement_place',
          objet_type: 'emplacement',
          objet_id: place.id,
          version_attendue: place.position_version,
          enregistre_le: new Date().toISOString(),
          charge,
        },
        {
          titre: 'Recensement de place',
          objet_libelle: place.reference,
          photos: versPhotosAEnregistrer(photos, 'emplacement'),
        },
      );
      await noterSaisie(emporte.ilot.id, place.id, {
        statut,
        occupant: statut === 'occupee' ? nom : null,
        enregistree_le: new Date().toISOString(),
      });
      onSaisie();
      setNom('');
      setTelephone('');
      setActivite('');
      setPhotos([]);
      setConfirme(false);
      setStatut('occupee');
      setIndex((i) => Math.min(i + 1, emporte.places.length - 1));
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Section
      titre="Recensement place par place"
      droite={
        <span className={styles.aide}>
          {index + 1} sur {emporte.places.length}
        </span>
      }
    >
      <div className={styles.pile}>
        <div className={styles.ligneBoutons}>
          <Bouton
            petit
            variante="secondaire"
            disabled={index === 0}
            onClick={() => setIndex(index - 1)}
          >
            Place précédente
          </Bouton>
          <Bouton
            petit
            variante="secondaire"
            disabled={index >= emporte.places.length - 1}
            onClick={() => setIndex(index + 1)}
          >
            Place suivante
          </Bouton>
        </div>
        <h3 className={styles.titreSection}>
          Place {place.reference}
          {place.rangee ? `, rangée ${place.rangee}, rang ${place.rang}` : ''}
        </h3>
        <p className={styles.aide}>
          Situation : {situation(place, emporte)}.
          {place.occupant_attribue ? ` Attribuée à ${place.occupant_attribue}.` : ''}
        </p>
        {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
        <div className={styles.choix} role="radiogroup" aria-label="Situation de la place">
          {(['occupee', 'libre'] as const).map((s) => (
            <label key={s}>
              <input
                type="radio"
                name="statut-place"
                value={s}
                checked={statut === s}
                onChange={() => setStatut(s)}
              />
              {s === 'occupee' ? 'Occupée' : 'Libre'}
            </label>
          ))}
        </div>
        {statut === 'occupee' ? (
          <>
            <Champ
              libelle="Nom de l’occupant"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              maxLength={200}
              required
            />
            <Champ
              libelle="Téléphone (facultatif)"
              type="tel"
              value={telephone}
              onChange={(e) => setTelephone(e.target.value)}
              maxLength={20}
            />
            <Champ
              libelle="Activité (facultative)"
              value={activite}
              onChange={(e) => setActivite(e.target.value)}
              maxLength={120}
            />
          </>
        ) : null}
        <SaisiePhotos photos={photos} onChange={setPhotos} max={1} libelle="Photo de la place" />
        <CaseACocher
          libelle={
            statut === 'occupee'
              ? 'Je confirme l’occupant présent sur cette place'
              : 'Je confirme que la place est libre'
          }
          checked={confirme}
          onChange={(e) => setConfirme(e.target.checked)}
        />
        <Bouton
          disabled={!confirme || enCours || (statut === 'occupee' && nom.trim().length < 2)}
          onClick={() => void enregistrer()}
        >
          Enregistrer et passer à la suivante
        </Bouton>
      </div>
    </Section>
  );
}
